/**
 * Liveblocks-backed canvas state for React.
 *
 * Wraps the canvas in a Liveblocks RoomProvider
 * (room id = `Canvas.liveblocks_room_id` from the API) and exposes nodes/edges/mutations
 * via `useCanvasLoro()`.
 *
 * NOTE: The hook is still exported as `useCanvasLoro` and the interface as
 * `CanvasContextType` for backward compatibility with the ~30 hook/component
 * files that import them from this module.
 */

import "../../../lib/liveblocks/liveblocks.config";

import { useCanvasById } from "@/api/canvas";
import { getApiErrorMessage } from "@/api/utils";
import { Button } from "@/components/ui/button";
import { CANVAS_INITIAL_PRESENCE } from "@/lib/liveblocks/canvasPresence";
import {
  trackCanvasEdgeRemoved,
  trackCanvasNodeAdded,
  trackCanvasNodeRemoved,
} from "@/utils/analytics";
import type { Canvas } from "@cohesive/api-client";
import { ErrorDisplay, Spinner } from "@cohesive/ui-components";
import { LiveObject } from "@liveblocks/client";
import {
  ClientSideSuspense,
  RoomProvider,
  useHistory,
  useMutation,
  useSelf,
  useStatus,
} from "@liveblocks/react";
import { useLiveblocksFlow } from "@liveblocks/react-flow";
import type {
  Edge,
  EdgeChange,
  Node,
  NodeChange,
  OnConnect,
  OnDelete,
  OnEdgesChange,
  OnNodesChange,
} from "@xyflow/react";
import { useReactFlow } from "@xyflow/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { consumeInsertSource } from "../analytics/insertSource";
import { useCanvasPermissions } from "../hooks/useCanvasPermissions";
import { WIDGET_ALL_PRESET_MAP } from "../nodes/widgets";
import {
  displayNodes,
  isRemovedWidget,
  planLegacyMigration,
  upgradeLegacyWidget,
} from "../nodes/widgets/legacyPresets";
import {
  connectorDataWithLocalEndpoints,
  type LineLabelStyle,
} from "../types/canvasNodeData";
import type { LinePoint, LineRouteFrame } from "../utils/linePath";
import { isNodeLocked } from "../utils/nodeLock";
import {
  buildDetachUpdatesForSectionRemovals,
  SectionParentUpdate,
} from "../utils/sectionMembership";
import { emitCanvasNodesDeleted } from "./canvasNodeDeleteEvents";
import { CanvasRoomConnectionErrors } from "./CanvasRoomConnectionErrors";
import { useCanvasRoute } from "./CanvasRouteContext";

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

/** Value supplied through the canvas context. */
export interface CanvasContextType {
  isReady: boolean;
  isLoading: boolean;
  error: Error | null;
  canEdit: boolean;
  canvas: Canvas | null;
  nodes: Node[];
  edges: Edge[];
  /** React Flow change handler — pass as `onNodesChange` to `<ReactFlow>`. */
  onNodesChange: OnNodesChange<Node>;
  onEdgesChange: OnEdgesChange<Edge>;
  /** Adds an edge when handles are connected. */
  onConnect: OnConnect;
  /** Called when nodes/edges are deleted via keyboard. */
  onDelete: OnDelete<Node, Edge>;
  /** No-op: Liveblocks handles remote updates automatically. */
  importRemoteUpdate: (update: Uint8Array) => void;
  addNode: (node: Node) => void;
  removeNode: (nodeId: string) => void;
  removeNodes: (nodeIds: string[]) => void;
  updateNodePosition: (
    nodeId: string,
    position: { x: number; y: number },
  ) => void;
  updateNodePositions: (
    updates: Array<{ id: string; position: { x: number; y: number } }>,
  ) => void;
  updateNodeData: (nodeId: string, data: Record<string, unknown>) => void;
  /** Delete one top-level key from a node's `data` subtree. `updateNodeData`
   * is merge-only and can never remove a key (Liveblocks has no
   * `set(key, undefined)`), so this is the removal primitive — e.g.
   * converting an HTML block back to a standard document by dropping
   * `data.presentation`. */
  removeNodeDataKey: (nodeId: string, key: string) => void;
  /** Wholesale-replace the entire `data` subtree of a node. Unlike `updateNodeData`
   * (which merges), this drops all existing keys and writes the provided object.
   * Used to finalize loading nodes: writing the real entity naturally drops
   * the `loading` flag without needing a separate delete step. */
  replaceNodeData: (nodeId: string, data: Record<string, unknown>) => void;
  updateNodeDimensions: (
    nodeId: string,
    dimensions: { width?: number; height?: number },
  ) => void;
  /** Drop a node's stored width so it sizes to its content again — a text
   * label goes back to hugging. Single-call gesture, so the full reconcile
   * that deletes the key is safe here (see `replaceNode`). */
  clearNodeWidth: (nodeId: string) => void;
  updateNodeZIndex: (nodeId: string, zIndex: number) => void;
  /** Hide/show a node on the canvas surface (React Flow's native `hidden`).
   * Hiding is the non-destructive counterpart to delete: the node — and, for a
   * file entity, the file's canvas membership — stays intact; the canvas just
   * stops rendering it. The rail lists hidden nodes with a "Show on canvas"
   * affordance. Persisted to Liveblocks like any other node field. */
  setNodeHidden: (nodeId: string, hidden: boolean) => void;
  /** Lock or unlock nodes (top-level `locked`, beside `hidden`). One undo
   * step per call. Unlock deletes the key: absent means unlocked. */
  setNodesLocked: (nodeIds: string[], locked: boolean) => void;
  /** Atomically update a node's parentId and position. Pass null to detach. */
  updateNodeParent: (
    nodeId: string,
    parentId: string | null,
    newPosition: { x: number; y: number },
  ) => void;
  /** Batch update parentId + position for N nodes in one commit. */
  batchUpdateNodeParents: (
    updates: Array<{
      nodeId: string;
      parentId: string | null;
      position: { x: number; y: number };
    }>,
  ) => void;
  /** Atomically update connector endpoints + bounding box. */
  updateConnectorEndpoints: (
    nodeId: string,
    endpoints: {
      start: { x: number; y: number };
      end: { x: number; y: number };
    },
    position: { x: number; y: number },
    dimensions: { width: number; height: number },
  ) => void;
  addEdge: (edge: {
    id: string;
    source: string;
    target: string;
    type?: string;
    sourceHandle?: string;
    targetHandle?: string;
    data?: Record<string, unknown>;
    // Native React Flow visual fields — the source of truth for edge styling
    // (data carries only curve_type).
    style?: Edge["style"];
    markerStart?: Edge["markerStart"];
    markerEnd?: Edge["markerEnd"];
    label?: Edge["label"];
    /** Carried through an in-place replace so an edit does not deselect the edge. */
    selected?: boolean;
    zIndex?: number;
  }) => void;
  removeEdge: (edgeId: string) => void;
  removeEdges: (edgeIds: string[]) => void;
  /**
   * Patch an edge in place: label text (blank clears), label typography,
   * route (bend, points, pinned sides; null clears each), or its end nodes.
   * Everything the patch does not name is carried through.
   */
  updateEdge: (edgeId: string, patch: EdgePatch) => void;
  /**
   * Run a callback whose storage mutations should NOT enter the undo/redo
   * stack. Use for deletions tied to irreversible side effects (e.g. removing
   * a canvas node alongside deleting the underlying file record) — bringing
   * such a node back via undo would resurrect a phantom pointing at a
   * now-missing resource.
   *
   * Thin wrapper around `room.history.disable(fn)`. Returns the callback's
   * return value; if it throws, history stacks are left unchanged.
   */
  withoutUndo: <T>(fn: () => T) => T;
  /**
   * Merge every storage mutation made from now until `resumeUndo()` into a
   * single undo step. For gestures that write repeatedly across pointer events
   * — the stroke eraser sweeping over several drawings — where a callback
   * wrapper can't span the whole gesture. Both calls are idempotent.
   *
   * Thin wrapper around `room.history.pause()` / `resume()`, the same pairing
   * `@liveblocks/react-flow` applies to a node drag.
   */
  pauseUndo: () => void;
  resumeUndo: () => void;
  /**
   * The node open full-size in the canvas lightbox, if any. Local UI state —
   * never persisted, never shared — kept here because every route that opens
   * it (node toolbar and menu, the Enter key, the detail pane through the
   * canvas:expand-node event) already reaches the canvas through this
   * context, the public viewer's stub included.
   */
  expandedNodeId: string | null;
  expandNode: (nodeId: string) => void;
  collapseNode: () => void;
  /** The signed-in collaborator; null outside a room (the public viewer). */
  self: CanvasSelf | null;
}

export interface CanvasSelf {
  id: string;
  name: string;
  avatar?: string;
}

/** Volatile document slice — nodes/edges and React Flow change handlers (~60Hz during drag). */
export type CanvasDocContextType = Pick<
  CanvasContextType,
  "nodes" | "edges" | "onNodesChange" | "onEdgesChange" | "onConnect"
>;

/** Stable mutations and metadata — safe for memoized node components. */
export type CanvasApiContextType = Omit<
  CanvasContextType,
  keyof CanvasDocContextType
>;

// ---------------------------------------------------------------------------
// Context + hooks
// ---------------------------------------------------------------------------

const CanvasApiContext = createContext<CanvasApiContextType | null>(null);
const CanvasDocContext = createContext<CanvasDocContextType | null>(null);

/** Raw API context `Provider` — prefer `CanvasLiveblocksProvider`; use in tests to inject API only. */
export const CanvasApiContextProvider = CanvasApiContext.Provider;

/** Raw doc context `Provider` — prefer `CanvasLiveblocksProvider`; use in tests to inject doc only. */
export const CanvasDocContextProvider = CanvasDocContext.Provider;

/** @deprecated Prefer `CanvasApiContextProvider` + `CanvasDocContextProvider`. */
export const CanvasContextProvider = CanvasApiContextProvider;

export type EdgePatch = {
  label?: string;
  label_style?: LineLabelStyle | null;
  bend?: { x: number; y: number } | null;
  points?: LinePoint[] | null;
  points_frame?: LineRouteFrame | null;
  source_side?: string | null;
  target_side?: string | null;
  source?: string;
  target?: string;
  /** 0 sends the edge under the nodes; null restores the default (just above them). */
  zIndex?: number | null;
};

const EDGE_DATA_PATCH_KEYS = [
  "label_style",
  "bend",
  "points",
  "points_frame",
  "source_side",
  "target_side",
] as const;

/** Stable canvas API (mutations, permissions) without subscribing to nodes/edges. */
export function useCanvasApi(): CanvasApiContextType {
  const ctx = useContext(CanvasApiContext);
  if (!ctx) {
    throw new Error(
      "useCanvasApi must be used within CanvasLiveblocksProvider",
    );
  }
  return ctx;
}

/** Liveblocks nodes/edges — re-renders on every document tick. */
export function useCanvasDoc(): CanvasDocContextType {
  const ctx = useContext(CanvasDocContext);
  if (!ctx) {
    throw new Error(
      "useCanvasDoc must be used within CanvasLiveblocksProvider",
    );
  }
  return ctx;
}

/** Full canvas context (API + document). Use in CanvasPage and drag hooks that need live nodes. */
export function useCanvas(): CanvasContextType {
  const api = useCanvasApi();
  const doc = useCanvasDoc();
  return useMemo(() => ({ ...api, ...doc }), [api, doc]);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Order nodes for React Flow on every Liveblocks sync.
 *
 * Two constraints (parent-first alone does not satisfy both):
 * 1. Each `parentId` target must appear before its children so
 *    `adoptUserNodes` can resolve absolute positions (nested sections).
 * 2. All `section` nodes before other types so section backdrops paint
 *    under free-floating entities.
 *
 * O(n): DFS parent-first pass, then stable section / non-section partition.
 */
function sortCanvasNodes(nodes: Node[]): Node[] {
  if (nodes.length < 2) return nodes;

  const byId = new Map<string, Node>();
  for (const node of nodes) byId.set(node.id, node);

  const parentFirst: Node[] = [];
  const visited = new Set<string>();

  const visit = (node: Node) => {
    if (visited.has(node.id)) return;
    visited.add(node.id);
    if (node.parentId) {
      const parent = byId.get(node.parentId);
      if (parent) visit(parent);
    }
    parentFirst.push(node);
  };

  for (const node of nodes) visit(node);

  let sectionCount = 0;
  for (const node of parentFirst) {
    if (node.type === "section") sectionCount++;
  }
  if (sectionCount === 0 || sectionCount === parentFirst.length) {
    return parentFirst;
  }

  const sections: Node[] = [];
  const rest: Node[] = [];
  for (const node of parentFirst) {
    if (node.type === "section") sections.push(node);
    else rest.push(node);
  }
  return sections.concat(rest);
}

// ---------------------------------------------------------------------------
// Read-only support
// ---------------------------------------------------------------------------
//
// `useLiveblocksFlow`'s change handlers write to Liveblocks Storage, which
// THROWS for users whose token lacks write access (view-only shares). React
// Flow emits changes even for pure viewing — `dimensions` from its
// ResizeObserver on load, `select` on click — so routing those into the
// Liveblocks handlers crashes the canvas for read-only users.
//
// For read-only sessions we swap in local handlers instead: `select` changes
// land in a local overlay (so click-selection still highlights), everything
// else is dropped. Dropping `dimensions` is safe — React Flow keeps
// measurements in its internal nodeLookup; the write-back to the nodes array
// is persistence-only.

/** Fold `select` changes into an id → selected overlay map; other change types are ignored. */
function applySelectChanges(
  prev: ReadonlyMap<string, boolean>,
  changes: ReadonlyArray<NodeChange<Node> | EdgeChange<Edge>>,
): ReadonlyMap<string, boolean> {
  let next: Map<string, boolean> | null = null;
  for (const change of changes) {
    if (change.type !== "select") continue;
    if ((next ?? prev).get(change.id) === change.selected) continue;
    next ??= new Map(prev);
    next.set(change.id, change.selected);
  }
  return next ?? prev;
}

/** Apply a selection overlay on top of storage-derived elements. */
function withSelectionOverlay<T extends Node | Edge>(
  elements: T[],
  overlay: ReadonlyMap<string, boolean>,
): T[] {
  if (overlay.size === 0) return elements;
  return elements.map((element) => {
    const selected = overlay.get(element.id);
    return selected === undefined || selected === (element.selected ?? false)
      ? element
      : { ...element, selected };
  });
}

const NOOP_ON_CONNECT: OnConnect = () => {};
const NOOP_ON_DELETE: OnDelete<Node, Edge> = () => {};

// ---------------------------------------------------------------------------
// Inner hook — must run inside a Liveblocks RoomProvider
// ---------------------------------------------------------------------------

function useCanvasLiveblocksDoc(canvasId: string): {
  api: CanvasApiContextType;
  doc: CanvasDocContextType;
} {
  const { data: canvas = null } = useCanvasById(canvasId);
  const { canEdit } = useCanvasPermissions();

  const {
    nodes: rawNodes,
    edges: rawEdges,
    onNodesChange: liveblocksOnNodesChange,
    onEdgesChange: liveblocksOnEdgesChange,
    onConnect: liveblocksOnConnect,
    onDelete: liveblocksOnDelete,
    isLoading,
  } = useLiveblocksFlow<Node, Edge>({ suspense: false });
  const { setNodes, getInternalNode } = useReactFlow<Node>();

  // Liveblocks' own write-permission signal (the token's `room:write`
  // scope) — the exact condition Storage writes assert on, so it can never
  // disagree with the code that throws. The API-derived `canEdit` above is
  // NOT a substitute: it starts `false` while its query is in flight, which
  // would briefly route an editor's changes to the read-only path.
  const canWrite = useSelf((me) => me.canWrite) ?? false;
  // Primitive selectors: cursor presence changes must not re-render widgets.
  const selfId = useSelf((me) => me.id);
  const selfName = useSelf((me) => me.info?.name);
  const selfAvatar = useSelf((me) => me.info?.avatar);
  const self = useMemo<CanvasSelf | null>(
    () =>
      selfId
        ? {
            id: selfId,
            name: selfName ?? "",
            ...(selfAvatar ? { avatar: selfAvatar } : {}),
          }
        : null,
    [selfId, selfName, selfAvatar],
  );

  // `useHistory()` returns the room's `History` object directly, so the
  // identity is stable across renders and `withoutUndo` doesn't need a
  // `useCallback` ref.
  const history = useHistory();

  // Read-only sessions: keep click-selection working via a local overlay
  // instead of Liveblocks Storage (see module comment on applySelectChanges).
  const [readOnlyNodeSelection, setReadOnlyNodeSelection] = useState<
    ReadonlyMap<string, boolean>
  >(() => new Map());
  const [readOnlyEdgeSelection, setReadOnlyEdgeSelection] = useState<
    ReadonlyMap<string, boolean>
  >(() => new Map());

  // The lightbox: which node is open full-size. Expand / collapse are rare
  // user gestures, so the api identity churn they cause is a non-issue.
  const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null);
  const expandNode = useCallback(
    (nodeId: string) => setExpandedNodeId(nodeId),
    [],
  );
  const collapseNode = useCallback(() => setExpandedNodeId(null), []);

  const readOnlyOnNodesChange = useCallback<OnNodesChange<Node>>(
    (changes) =>
      setReadOnlyNodeSelection((prev) => applySelectChanges(prev, changes)),
    [],
  );
  const readOnlyOnEdgesChange = useCallback<OnEdgesChange<Edge>>(
    (changes) =>
      setReadOnlyEdgeSelection((prev) => applySelectChanges(prev, changes)),
    [],
  );

  // React Flow REQUIRES parents to appear before children in the nodes
  // array — otherwise `adoptUserNodes`/`updateChildNode` can't find the
  // parent in `nodeLookup`, child positions are never offset by the
  // parent's absolute position (children render at raw relative coords,
  // i.e. jump to top-left), and the child is never added to
  // `parentLookup` (so parent drags don't propagate to children).
  //
  // Liveblocks's `useLiveblocksFlow` returns nodes via
  // `Object.values(nodesMap)` — i.e. LiveMap insertion order — which
  // does NOT reflect parent-child order (a section drawn over existing
  // nodes is inserted AFTER the nodes it adopts).
  //
  const nodes = useMemo<Node[]>(() => {
    // Removed widgets never render. Read-only sessions see a retro board as
    // the notes an editor's load will write; editors see nothing until their
    // own migration below stores the real notes, so no edit lands on a
    // preview note that isn't in storage.
    const sorted = sortCanvasNodes(
      displayNodes(rawNodes ?? [], { previewRetro: !canWrite }),
    );
    return canWrite
      ? sorted
      : withSelectionOverlay(sorted, readOnlyNodeSelection);
  }, [rawNodes, canWrite, readOnlyNodeSelection]);
  const edges = useMemo<Edge[]>(() => {
    // A line paints above the nodes it joins unless a node was brought to
    // front (nodes win a tie) or the edge was sent to back (zIndex 0).
    const base = ((rawEdges ?? []) as Edge[]).map((e) =>
      e.zIndex === undefined ? { ...e, zIndex: 1 } : e,
    );
    return canWrite ? base : withSelectionOverlay(base, readOnlyEdgeSelection);
  }, [rawEdges, canWrite, readOnlyEdgeSelection]);

  // Liveblocks pauses history for a local drag or resize, and anything
  // written meanwhile joins that gesture's undo entry, even under
  // history.disable. The lazy migration waits these out.
  const gestureNodesRef = useRef(new Set<string>());
  const undoPausedRef = useRef(false);
  const [gestureActive, setGestureActive] = useState(false);
  const syncGestureActive = useCallback(() => {
    setGestureActive(gestureNodesRef.current.size > 0 || undoPausedRef.current);
  }, []);
  const trackedOnNodesChange = useCallback<OnNodesChange<Node>>(
    (changes) => {
      const gesture = gestureNodesRef.current;
      const before = gesture.size;
      for (const c of changes) {
        const flag =
          c.type === "position"
            ? c.dragging
            : c.type === "dimensions"
              ? c.resizing
              : undefined;
        if (flag === undefined || !("id" in c)) continue;
        if (flag) gesture.add(c.id);
        else gesture.delete(c.id);
      }
      (liveblocksOnNodesChange as OnNodesChange<Node>)(changes);
      if (before > 0 !== gesture.size > 0) syncGestureActive();
    },
    [liveblocksOnNodesChange, syncGestureActive],
  );

  // For read-only users, never call the Liveblocks-backed handlers — every
  // storage write throws for them. All programmatic mutation helpers below
  // route through these, so they become safe no-ops automatically.
  const onNodesChange = canWrite ? trackedOnNodesChange : readOnlyOnNodesChange;
  const onEdgesChange = canWrite
    ? (liveblocksOnEdgesChange as OnEdgesChange<Edge>)
    : readOnlyOnEdgesChange;
  const onConnect = canWrite ? liveblocksOnConnect : NOOP_ON_CONNECT;
  const onDeleteStorage = canWrite ? liveblocksOnDelete : NOOP_ON_DELETE;
  const batchUpdateNodeParents = useCallback(
    (updates: SectionParentUpdate[]) => {
      if (updates.length === 0) return;
      setNodes((current) => {
        const byId = new Map(current.map((n) => [n.id, { ...n }]));
        for (const update of updates) {
          const node = byId.get(update.nodeId);
          if (!node) continue;
          const next: Node = {
            ...node,
            position: update.position,
            parentId: update.parentId ?? undefined,
          };
          byId.set(update.nodeId, next);
        }
        return sortCanvasNodes([...byId.values()]);
      });
    },
    [setNodes],
  );

  // Announce before the delete lands: comment threads anchored to these nodes
  // detach rather than dying with them, and freezing each pin needs the node's
  // absolute position while it is still in the store. Also the single seam
  // for delete analytics — every removal path (trash buttons, keyboard,
  // section delete, React Flow's onDelete) funnels through here, which makes
  // it the lock guard too: a locked node is never removed, and a locked child
  // of a section deleted with its contents is detached in place.
  const onDelete = useCallback<OnDelete<Node, Edge>>(
    (requested) => {
      const params = {
        ...requested,
        nodes: (requested.nodes ?? []).filter((n) => !isNodeLocked(n)),
      };
      const removedIds = params.nodes.map((n) => n.id);
      const lockedIds = new Set(
        nodesRef.current.filter(isNodeLocked).map((n) => n.id),
      );
      const detach = buildDetachUpdatesForSectionRemovals(
        removedIds,
        nodesRef.current,
        getInternalNode,
      ).filter((u) => lockedIds.has(u.nodeId));
      if (detach.length > 0) batchUpdateNodeParents(detach);
      emitCanvasNodesDeleted(removedIds);
      if (canWrite) {
        const nodes = params.nodes ?? [];
        const edges = params.edges ?? [];
        if (nodes.length > 0) {
          trackCanvasNodeRemoved({
            count: nodes.length,
            nodeTypes: [...new Set(nodes.map((n) => n.type ?? "unknown"))],
          });
        }
        if (edges.length > 0) {
          trackCanvasEdgeRemoved({ count: edges.length });
        }
      }
      onDeleteStorage(params);
    },
    [canWrite, onDeleteStorage, getInternalNode, batchUpdateNodeParents],
  );

  // Strip removed widgets from storage on load: the lazy migration. Retro
  // boards holding cards become sticky notes, and old decorative presets are
  // rewritten onto their stickers, in one batch planned from live storage,
  // so a node a collaborator just deleted or a note they already edited is
  // left alone. Kept out of undo history: undoing past a recorded strip
  // would resurrect the node and the re-fired delete wipes the redo stack.
  // Deferred while a local gesture holds history paused. Read-only sessions
  // never write; until an editor opens the canvas, `nodes` shows retro
  // boards as their notes.
  const migrateLegacyNodes = useMutation(
    ({ storage, self }) => {
      if (!self.canWrite) return;
      const flow = storage.get("flow" as never) as
        | { get(key: string): unknown }
        | undefined;
      if (!flow || typeof flow.get !== "function") return;
      const nodesMap = flow.get("nodes") as
        | { values(): IterableIterator<{ toJSON(): unknown }> }
        | undefined;
      const edgesMap = flow.get("edges") as
        | { values(): IterableIterator<{ toJSON(): unknown }> }
        | undefined;
      if (!nodesMap || !edgesMap) return;
      const live = [...nodesMap.values()].map((n) => n.toJSON() as Node);
      const { add, replace, remove } = planLegacyMigration(
        live,
        WIDGET_ALL_PRESET_MAP,
      );
      if (add.length > 0) {
        liveblocksOnNodesChange(add.map((item) => ({ type: "add", item })));
      }
      if (replace.length > 0) {
        liveblocksOnNodesChange(
          replace.map((item) => ({ type: "replace", id: item.id, item })),
        );
      }
      // Edges touching a removed widget go too: the UI never wired widget
      // handles, but the canvas edit tool only checks that endpoints exist.
      if (remove.length > 0) {
        const ids = new Set(remove.map((n) => n.id));
        const edges = [...edgesMap.values()]
          .map((e) => e.toJSON() as Edge)
          .filter((e) => ids.has(e.source) || ids.has(e.target));
        liveblocksOnDelete({ nodes: remove, edges });
      }
    },
    [liveblocksOnNodesChange, liveblocksOnDelete],
  );
  // rawNodes only populates once the `flow` storage object exists, so the
  // write cannot land in the dropped-write window a too-early mutation would.
  useEffect(() => {
    if (!canWrite || gestureActive) return;
    const pending = (rawNodes ?? []).some(
      (n) =>
        isRemovedWidget(n) || upgradeLegacyWidget(n, WIDGET_ALL_PRESET_MAP),
    );
    if (pending) history.disable(() => migrateLegacyNodes());
  }, [canWrite, gestureActive, rawNodes, history, migrateLegacyNodes]);

  // Keep stable refs so mutation callbacks read the latest arrays.
  const nodesRef = useRef<Node[]>(nodes);
  nodesRef.current = nodes;
  const edgesRef = useRef<Edge[]>(edges);
  edgesRef.current = edges;

  // ------------------------------------------------------------------
  // Mutation helpers — implemented via onNodesChange / onEdgesChange
  // ------------------------------------------------------------------

  const addNode = useCallback(
    (node: Node) => {
      trackCanvasNodeAdded({
        nodeType: node.type,
        source: consumeInsertSource() ?? "toolbar",
        canvasId,
      });
      onNodesChange([{ type: "add", item: node }]);
    },
    [onNodesChange, canvasId],
  );

  // `@liveblocks/react-flow` ignores `"remove"` NodeChange/EdgeChange
  // entries entirely (its internal applyNodeChanges has `case "remove":
  // break;`). Deletes are persisted only through the separate `onDelete`
  // mutation — the same one we wire to `<ReactFlow onDelete={...}>`.
  // We reuse it for every programmatic removal path so trash buttons,
  // keyboard shortcuts (which preventDefault React Flow's built-in
  // handler), and section deletion all actually delete from storage.
  //
  const removeNode = useCallback(
    (nodeId: string) => {
      const node = nodesRef.current.find((n) => n.id === nodeId);
      if (!node) return;
      onDelete({
        nodes: [node],
        edges: [],
      });
    },
    [onDelete],
  );

  const removeNodes = useCallback(
    (nodeIds: string[]) => {
      if (nodeIds.length === 0) return;
      const idSet = new Set(nodeIds);
      const toDelete = nodesRef.current.filter((n) => idSet.has(n.id));
      if (toDelete.length === 0) return;
      onDelete({
        nodes: toDelete,
        edges: [],
      });
    },
    [onDelete],
  );

  const addEdge = useCallback(
    (edge: {
      id: string;
      source: string;
      target: string;
      type?: string;
      sourceHandle?: string;
      targetHandle?: string;
      data?: Record<string, unknown>;
      style?: Edge["style"];
      markerStart?: Edge["markerStart"];
      markerEnd?: Edge["markerEnd"];
      label?: Edge["label"];
      selected?: boolean;
      zIndex?: number;
    }) => onEdgesChange([{ type: "add", item: edge as Edge }]),
    [onEdgesChange],
  );

  const removeEdge = useCallback(
    (edgeId: string) => {
      const edge = edgesRef.current.find((e) => e.id === edgeId);
      if (!edge) return;
      onDelete({
        nodes: [],
        edges: [edge],
      });
    },
    [onDelete],
  );

  // `add` is a full edge replace in storage, so every native field is carried
  // through; only what the patch names changes.
  const updateEdge = useCallback(
    (edgeId: string, patch: EdgePatch) => {
      const e = edgesRef.current.find((x) => x.id === edgeId);
      if (!e) return;
      const data = { ...((e.data ?? {}) as Record<string, unknown>) };
      for (const key of EDGE_DATA_PATCH_KEYS) {
        if (!(key in patch)) continue;
        const value = patch[key];
        if (value === null || value === undefined) delete data[key];
        else data[key] = value;
      }
      const label =
        patch.label === undefined ? e.label : patch.label.trim() || undefined;
      // A pinned side names the handle that side renders; a re-attached end
      // must point at a handle its new node has, or React Flow drops the edge.
      const sourceHandle = patch.source_side
        ? `${patch.source_side}-source`
        : e.sourceHandle;
      const targetHandle = patch.target_side
        ? `${patch.target_side}-source`
        : e.targetHandle;
      addEdge({
        id: edgeId,
        source: patch.source ?? e.source,
        target: patch.target ?? e.target,
        type: e.type,
        sourceHandle: sourceHandle ?? undefined,
        targetHandle: targetHandle ?? undefined,
        style: e.style,
        markerStart: e.markerStart,
        markerEnd: e.markerEnd,
        data,
        label,
        selected: e.selected,
        zIndex:
          patch.zIndex === undefined ? e.zIndex : (patch.zIndex ?? undefined),
      });
    },
    [addEdge],
  );

  const removeEdges = useCallback(
    (edgeIds: string[]) => {
      if (edgeIds.length === 0) return;
      const idSet = new Set(edgeIds);
      const toDelete = edgesRef.current.filter((e) => idSet.has(e.id));
      if (toDelete.length === 0) return;
      onDelete({
        nodes: [],
        edges: toDelete,
      });
    },
    [onDelete],
  );

  // Position/dimension updates from React Flow's interactive paths (drag,
  // resize handle) already round-trip through liveblocksOnNodesChange — we
  // don't need to do anything for those. But programmatic callers (section
  // collapse, resize-to-fit, snap-to-grid, etc.) bypass React Flow entirely
  // and must dispatch their own NodeChange entries to land in storage.
  //
  // We use the canonical change types (`position`, `dimensions`) instead of
  // a `replace` so Liveblocks's atomic field setters run and we don't clobber
  // unrelated local-only state (selection, dragging, measured).
  //
  // For dimensions, `setAttributes` controls which fields actually persist;
  // we map the partial input shape to the appropriate value so callers can
  // update width-only or height-only without zeroing the other.
  const updateNodePosition = useCallback(
    (id: string, position: { x: number; y: number }) =>
      onNodesChange([{ type: "position", id, position }]),
    [onNodesChange],
  );
  const updateNodePositions = useCallback(
    (updates: Array<{ id: string; position: { x: number; y: number } }>) => {
      if (updates.length === 0) return;
      onNodesChange(
        updates.map(({ id, position }) => ({
          type: "position" as const,
          id,
          position,
        })),
      );
    },
    [onNodesChange],
  );
  const updateNodeDimensions = useCallback(
    (id: string, dims: { width?: number; height?: number }) => {
      const hasW = dims.width !== undefined;
      const hasH = dims.height !== undefined;
      if (!hasW && !hasH) return;
      const setAttributes: true | "width" | "height" =
        hasW && hasH ? true : hasW ? "width" : "height";
      onNodesChange([
        {
          type: "dimensions",
          id,
          dimensions: { width: dims.width ?? 0, height: dims.height ?? 0 },
          setAttributes,
        },
      ]);
    },
    [onNodesChange],
  );

  // ⚠️  `replaceNode` reads the node from `nodesRef.current`, which only
  // refreshes when React re-renders. Two synchronous calls in the same
  // tick both see the *same* stale node. The dispatched "replace" change
  // triggers a Liveblocks `reconcile` with extent: "full" — meaning any
  // key on the LiveObject that isn't present in the new item is DELETED.
  // Net effect: call N+1 silently clobbers fields written by call N.
  //
  // Safe to use for: single-call-per-action mutations (z-index toggle,
  // section auto-organize commit, connector endpoints update).
  // UNSAFE for: anything that may fire back-to-back with a sibling update
  // touching different keys (e.g., line-style + line-width pickers).
  // For those, use a `useMutation`-backed primitive that writes against
  // live storage instead — see `updateNodeData` below.
  const replaceNode = useCallback(
    (updater: (node: Node) => Node, nodeId: string) => {
      const node = nodesRef.current.find((n) => n.id === nodeId);
      if (!node) return;
      onNodesChange([{ type: "replace", id: nodeId, item: updater(node) }]);
    },
    [onNodesChange],
  );

  // Atomic per-key updates against the LiveObject's `data` field. Reads
  // live storage (not a React ref), so consecutive calls in the same tick
  // compose correctly — call N's writes are visible to call N+1, and
  // neither clobbers keys set by the other.
  //
  // This is a deliberate departure from the `replaceNode` pattern: that
  // pattern dispatches a "replace" NodeChange and lets Liveblocks's full
  // reconcile rebuild the data sub-object, which loses any keys missing
  // from the (stale) snapshot. Shape floating toolbars fire two
  // `updateNodeData` calls back-to-back
  // (style + width) and were silently clobbering each other under the
  // old implementation.
  //
  // The `data` field is created as a LiveObject by Liveblocks's
  // `toLiveblocksInternalNode` (its sync config has no entry for `data`,
  // so `deepLiveify` defaults to liveifying plain objects). We branch on
  // `instanceof LiveObject` defensively — if a future schema change ever
  // makes `data` an atomic JSON blob, we fall back to whole-object
  // replacement so the call still does the right thing (with the
  // staleness caveat reintroduced for that branch only).
  const updateNodeData = useMutation(
    ({ storage, self }, nodeId: string, patch: Record<string, unknown>) => {
      // Writes throw for read-only users; drop silently like the
      // onNodesChange swap above.
      if (!self.canWrite) return;
      // Storage shape is owned by `useLiveblocksFlow` and intentionally
      // not declared in the global Liveblocks types (see
      // liveblocks.config.ts). We escape the type system here — both at
      // the storage key (`"flow"`) and on the LiveObject returns — and
      // rely on runtime `instanceof LiveObject` for the only branch that
      // matters for correctness.
      const flow = storage.get("flow" as never) as
        | { get(key: string): unknown }
        | undefined;
      if (!flow || typeof flow.get !== "function") return;
      const nodes = flow.get("nodes") as
        | { get(id: string): unknown }
        | undefined;
      const node = nodes?.get?.(nodeId) as
        | {
            get(key: string): unknown;
            set(key: string, value: unknown): void;
          }
        | undefined;
      if (!node) return;
      const data = node.get("data");
      if (data instanceof LiveObject) {
        // Dedupe primitive equals (`===`). Without this, callers that fire
        // on `OnChange`-style listeners — Lexical's `OnChangePlugin` runs on
        // any editor-state change including selection-only — push storage
        // writes for keys whose value didn't actually change. Each write
        // bumps the undo stack AND wipes the redo stack, so e.g. clicking
        // into a text node was destroying the user's redo history.
        //
        // We compare with `===` only: nested objects/arrays in `data` come
        // out of storage as `LiveObject` / `LiveList`, and incoming patch
        // values are plain JS — `===` is always false there, so they fall
        // through to a write (the safe default). This catches the common
        // case (booleans, numbers, color/font strings) which is enough.
        const liveData = data as unknown as {
          get(key: string): unknown;
          set(key: string, value: unknown): void;
        };
        for (const [key, value] of Object.entries(patch)) {
          if (liveData.get(key) === value) continue;
          liveData.set(key, value);
        }
        return;
      }
      // Fallback: `data` is an atomic JSON blob (not a LiveObject). The
      // staleness footgun documented above on `replaceNode` applies here —
      // we accept it because this branch is reachable only via an explicit
      // schema choice that opts out of LiveObject sync.
      node.set("data", {
        ...((data as Record<string, unknown> | undefined) ?? {}),
        ...patch,
      });
    },
    [],
  );

  const removeNodeDataKey = useMutation(
    ({ storage, self }, nodeId: string, key: string) => {
      if (!self.canWrite) return;
      const flow = storage.get("flow" as never) as
        | { get(key: string): unknown }
        | undefined;
      if (!flow || typeof flow.get !== "function") return;
      const nodes = flow.get("nodes") as
        | { get(id: string): unknown }
        | undefined;
      const node = nodes?.get?.(nodeId) as
        | {
            get(key: string): unknown;
            set(key: string, value: unknown): void;
          }
        | undefined;
      if (!node) return;
      const data = node.get("data");
      if (data instanceof LiveObject) {
        (data as unknown as { delete(key: string): void }).delete(key);
        return;
      }
      // Atomic-blob fallback: rebuild without the key (same staleness caveat
      // as the updateNodeData fallback above).
      const plain = {
        ...((data as Record<string, unknown> | undefined) ?? {}),
      };
      delete plain[key];
      node.set("data", plain);
    },
    [],
  );

  const replaceNodeData = useMutation(
    ({ storage, self }, nodeId: string, newData: Record<string, unknown>) => {
      if (!self.canWrite) return;
      const flow = storage.get("flow" as never) as
        | { get(key: string): unknown }
        | undefined;
      if (!flow || typeof flow.get !== "function") return;
      const nodes = flow.get("nodes") as
        | { get(id: string): unknown }
        | undefined;
      const node = nodes?.get?.(nodeId) as
        | {
            get(key: string): unknown;
            set(key: string, value: unknown): void;
          }
        | undefined;
      if (!node) return;
      node.set("data", newData);
    },
    [],
  );

  const clearNodeWidth = useCallback(
    (nodeId: string) =>
      replaceNode(({ width: _width, ...rest }) => rest, nodeId),
    [replaceNode],
  );

  const updateNodeZIndex = useCallback(
    (nodeId: string, zIndex: number) =>
      replaceNode((n) => ({ ...n, zIndex }), nodeId),
    [replaceNode],
  );

  const setNodeHidden = useCallback(
    (nodeId: string, hidden: boolean) =>
      replaceNode((n) => ({ ...n, hidden }), nodeId),
    [replaceNode],
  );

  // Live storage, not `replaceNode`: a lock toggle can follow any other write
  // in the same tick, and a stale full reconcile would undo it.
  const setNodesLocked = useMutation(
    ({ storage, self }, nodeIds: string[], locked: boolean) => {
      if (!self.canWrite) return;
      const flow = storage.get("flow" as never) as
        | { get(key: string): unknown }
        | undefined;
      if (!flow || typeof flow.get !== "function") return;
      const nodes = flow.get("nodes") as
        | { get(id: string): unknown }
        | undefined;
      for (const nodeId of nodeIds) {
        const node = nodes?.get?.(nodeId) as
          | {
              get(key: string): unknown;
              set(key: string, value: unknown): void;
              delete(key: string): void;
            }
          | undefined;
        if (!node) continue;
        if (locked) {
          if (node.get("locked") !== true) node.set("locked", true);
        } else if (node.get("locked") !== undefined) {
          node.delete("locked");
        }
      }
    },
    [],
  );

  const updateNodeParent = useCallback(
    (
      nodeId: string,
      parentId: string | null,
      newPosition: { x: number; y: number },
    ) =>
      replaceNode(
        (n) => ({
          ...n,
          parentId: parentId ?? undefined,
          position: newPosition,
        }),
        nodeId,
      ),
    [replaceNode],
  );

  const updateConnectorEndpoints = useCallback(
    (
      nodeId: string,
      endpoints: {
        start: { x: number; y: number };
        end: { x: number; y: number };
      },
      position: { x: number; y: number },
      dimensions: { width: number; height: number },
    ) =>
      replaceNode(
        (n) => ({
          ...n,
          position,
          width: dimensions.width,
          height: dimensions.height,
          data: connectorDataWithLocalEndpoints(
            (n.data ?? {}) as Record<string, unknown>,
            endpoints.start,
            endpoints.end,
          ),
        }),
        nodeId,
      ),
    [replaceNode],
  );

  const importRemoteUpdate = useCallback((_: Uint8Array) => {
    // No-op: Liveblocks handles remote state automatically.
  }, []);

  const withoutUndo = useCallback(
    <T,>(fn: () => T): T => history.disable(fn),
    [history],
  );
  const pauseUndo = useCallback(() => {
    undoPausedRef.current = true;
    syncGestureActive();
    history.pause();
  }, [history, syncGestureActive]);
  const resumeUndo = useCallback(() => {
    history.resume();
    undoPausedRef.current = false;
    syncGestureActive();
  }, [history, syncGestureActive]);

  const api = useMemo<CanvasApiContextType>(
    () => ({
      isReady: !isLoading,
      isLoading: isLoading ?? false,
      error: null,
      canEdit,
      canvas,
      onDelete,
      importRemoteUpdate,
      addNode,
      removeNode,
      removeNodes,
      addEdge,
      removeEdge,
      removeEdges,
      updateEdge,
      updateNodePosition,
      updateNodePositions,
      updateNodeData,
      removeNodeDataKey,
      replaceNodeData,
      updateNodeDimensions,
      clearNodeWidth,
      updateNodeZIndex,
      setNodeHidden,
      setNodesLocked,
      updateNodeParent,
      batchUpdateNodeParents,
      updateConnectorEndpoints,
      withoutUndo,
      pauseUndo,
      resumeUndo,
      expandedNodeId,
      expandNode,
      collapseNode,
      self,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isLoading,
      canEdit,
      canvas,
      onDelete,
      importRemoteUpdate,
      addNode,
      removeNode,
      removeNodes,
      addEdge,
      removeEdge,
      removeEdges,
      updateEdge,
      updateNodePosition,
      updateNodePositions,
      updateNodeData,
      removeNodeDataKey,
      replaceNodeData,
      updateNodeDimensions,
      clearNodeWidth,
      updateNodeZIndex,
      setNodeHidden,
      setNodesLocked,
      updateNodeParent,
      batchUpdateNodeParents,
      updateConnectorEndpoints,
      withoutUndo,
      pauseUndo,
      resumeUndo,
      expandedNodeId,
      expandNode,
      collapseNode,
      self,
    ],
  );

  const doc = useMemo<CanvasDocContextType>(
    () => ({
      nodes,
      edges,
      onNodesChange,
      onEdgesChange,
      onConnect,
    }),
    [nodes, edges, onNodesChange, onEdgesChange, onConnect],
  );

  return { api, doc };
}

// ---------------------------------------------------------------------------
// Inner component (inside RoomProvider, can call useLiveblocksFlow)
// ---------------------------------------------------------------------------

function Loader() {
  return (
    <div className="w-full h-full flex items-center justify-center bg-background">
      <Spinner size="lg" />
    </div>
  );
}

function CanvasLiveblocksInner({
  canvasId,
  children,
}: {
  canvasId: string;
  children: ReactNode;
}) {
  const { api, doc } = useCanvasLiveblocksDoc(canvasId);
  return (
    <CanvasApiContext.Provider value={api}>
      <CanvasDocContext.Provider value={doc}>
        {children}
      </CanvasDocContext.Provider>
    </CanvasApiContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Public provider
// ---------------------------------------------------------------------------

/**
 * Wraps the canvas route in a Liveblocks room and provides `useCanvasLoro()`
 * to all descendants. Replaces the old `CanvasLoroProvider`.
 */
export function CanvasLiveblocksProvider({
  children,
  uiVariant = "interactive",
}: {
  children: ReactNode;
  /** Thumbnail / headless capture skips connection error overlay. */
  uiVariant?: "interactive" | "capture" | "view";
}) {
  // No Auth0 dependency here: `useCanvasById` calls `orgAPI.canvas.getById`,
  // which derives org/user from whichever bearer token `orgAPI` has been
  // pointed at. In the main app that's Auth0 (`AuthInject`); in the
  // canvas-capture entry it's the short-lived capture JWT
  // (`CaptureOnlyApiInject`). Gating this provider on an Auth0-derived
  // `orgId` used to wedge the capture page on `<Loader />` forever because
  // the capture entry has no `Auth0Provider` — leave the api-client
  // interceptor to handle token readiness.
  const { canvasId } = useCanvasRoute();
  const showConnectionErrorOverlay = uiVariant !== "capture";

  const canvasQuery = useCanvasById(canvasId);

  if (!canvasId) {
    return (
      <div className="h-full w-full min-h-0 bg-background">
        <ErrorDisplay
          title="No canvas selected"
          message="Open a canvas from your workspace or the sidebar to view it here."
        />
      </div>
    );
  }

  if (canvasQuery.isPending) {
    return <Loader />;
  }

  if (canvasQuery.isError) {
    return (
      <div className="h-full w-full min-h-0 bg-background">
        <ErrorDisplay
          title="Could not load canvas"
          message={getApiErrorMessage(canvasQuery.error)}
          footer={
            <Button type="button" onClick={() => window.location.reload()}>
              Refresh page
            </Button>
          }
        />
      </div>
    );
  }

  const canvas = canvasQuery.data;
  const roomId = canvas?.liveblocks_room_id?.trim() ?? "";
  if (!canvas || !roomId) {
    return (
      <div className="h-full w-full min-h-0 bg-background">
        <ErrorDisplay
          title="Canvas unavailable"
          message="This canvas is missing collaboration room information. Try refreshing the page."
          footer={
            <Button type="button" onClick={() => window.location.reload()}>
              Refresh page
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <RoomProvider id={roomId} initialPresence={CANVAS_INITIAL_PRESENCE}>
      <CanvasRoomConnectionErrors
        roomId={roomId}
        showOverlay={showConnectionErrorOverlay}
      />
      <ClientSideSuspense fallback={<Loader />}>
        <CanvasLiveBlocksRoomLoading>
          <CanvasLiveblocksInner canvasId={canvasId}>
            {children}
          </CanvasLiveblocksInner>
        </CanvasLiveBlocksRoomLoading>
      </ClientSideSuspense>
    </RoomProvider>
  );
}

// Shows a loading spinner while the room is loading (websocket connection)
// or the room is initializing (first time connecting).
export function CanvasLiveBlocksRoomLoading({
  children,
}: {
  children: ReactNode;
}) {
  const status = useStatus();

  if (status === "connecting" || status === "initial") {
    return <Loader />;
  }

  return children;
}
